import { z } from 'zod';
import {
  AnalysisLensSchema,
  type CanonicalObjectType,
  CanonicalObjectTypeSchema,
  ComponentTypeSchema,
  type Domain,
  DomainSchema,
  PredicateBasisSchema,
  StrideCategorySchema,
} from './enums';
import { PackIdSchema, QrsControlIdSchema, ThreatPatternIdSchema } from './ids';
import {
  type AttributeRef,
  componentTypesNamed,
  describePredicate,
  type PredicateExpr,
  PredicateExprSchema,
  reachableObjectTypes,
  unconstrainedControlReads,
  conflatingTristateReads,
  validatePredicate,
  validateSubjectType,
} from './predicate';
import { NonEmptyStringSchema, SemVerSchema } from './primitives';
import { PatternScoringInputsSchema } from './scoring';

/**
 * Threat packs (DESIGN_SPEC §8).
 *
 * A pack is structured domain knowledge the LLM reasons *with* — not a prompt.
 * It supplies applicability conditions, the stable `threat_pattern_id` anchor
 * that makes reproducibility measurable, and the links onward to controls and
 * recommendation templates. Deterministic pre-selection narrows the candidate
 * set before any model sees it (§8.3), and since sds-04 the applicability
 * decision is made entirely in code: the pack service emits `PatternMatch`
 * objects carrying no prose at all, and contextualising them is the whole of
 * the LLM worker's job (docs/DECISIONS.md ZD-031).
 */

/**
 * What to do when the predicate cannot be decided (docs/DECISIONS.md ZD-029 §4).
 *
 * **Required, with no default, and the absence of a default is the decision.**
 * DESIGN_SPEC §8.3 rule 3 says a worker must not choose between a false finding
 * and silence — but which of the two honest alternatives is right depends
 * entirely on the pattern. A pattern reading `internet_facing` would raise a
 * conditional finding against every component of a poor-tier model, which is
 * the noise failure §2.2 names; a pattern where the unknown *is* the finding
 * has to speak. ZD-019's question has no "the author did not think about it"
 * answer here, so the author picks.
 */
export const UNKNOWN_POLICIES = [
  /** Emit a conditional finding, plus a clarification naming what was unresolved. */
  'conditional_finding',
  /** Ask the question; do not put a finding in front of a reviewer. */
  'clarification_only',
  /** Neither. For patterns whose unknown case is genuinely uninteresting. */
  'silent',
] as const;
export const UnknownPolicySchema = z.enum(UNKNOWN_POLICIES);
export type UnknownPolicy = z.infer<typeof UnknownPolicySchema>;

/**
 * A signal that strengthens a pattern without being required (§8.4).
 *
 * Typed like the predicate rather than left as prose, because
 * `predicate_basis: 'boost'` is otherwise unreachable: nothing could tell
 * whether a boost held. A boost contributes witnesses only when it is what
 * carried the match (ZD-029 §5).
 */
export const BoostSchema = z.object({
  /** What this boost means, for a reviewer. Rendered prose comes from the expression. */
  title: NonEmptyStringSchema,
  when: PredicateExprSchema,
});
export type Boost = z.infer<typeof BoostSchema>;

const ApplicabilityShape = z.object({
  /**
   * The object type the pattern is *about*. The pack service enumerates the
   * model's objects of this type and evaluates the predicate once per object,
   * so it is also the finding's primary grounding.
   */
  subject_type: CanonicalObjectTypeSchema,

  /**
   * Object types that must be present for this pattern to be a candidate at
   * all. The pack service filters on this deterministically and cheaply, before
   * evaluating anything, which is what keeps pre-selection reproducible.
   */
  requires_object_types: z.array(CanonicalObjectTypeSchema).min(1),

  /**
   * Component types that must be present too — the second half of
   * pre-selection, and it is load-bearing rather than an optimisation.
   *
   * Every model has components, so filtering on the object type alone lets an
   * AI pattern survive pre-selection against a ferry booking platform. It then
   * evaluates to `false` most of the time and does no harm — but where some
   * other clause is undecidable it evaluates to `unknown`, and the pattern
   * raises a conditional finding about a vector store the system does not
   * contain. That is the noise failure DESIGN_SPEC §2.2 names, produced by a
   * filter that was never fine enough.
   *
   * Checked against the predicate at parse: a type may be required only if the
   * pattern actually tests for it, so this cannot drift into an arbitrary
   * filter that silently narrows a pattern's reach.
   */
  requires_component_types: z.array(ComponentTypeSchema),

  /**
   * The applicability condition, as a typed three-valued expression
   * (docs/DECISIONS.md ZD-029). Text until sds-04, and parsed by nothing.
   */
  predicate: PredicateExprSchema,

  boosts: z.array(BoostSchema),
});
export type Applicability = z.infer<typeof ApplicabilityShape>;

/**
 * Everything about a pattern's applicability that can be wrong at authoring
 * time is refused here, at the pack's own parse.
 *
 * Each of these is a pattern that would otherwise be silently unsatisfiable —
 * never firing, with nothing saying why — which is the worst failure shape for
 * a pack: it looks like a threat that does not apply to this system.
 */
export const ApplicabilitySchema = ApplicabilityShape.superRefine((applicability, ctx) => {
  for (const issue of validateSubjectType(applicability.subject_type)) {
    ctx.addIssue({ code: 'custom', message: issue.message, path: issue.path });
  }
  for (const issue of [
    ...validatePredicate(applicability.predicate, applicability.subject_type),
    ...unconstrainedControlReads(applicability.predicate, applicability.subject_type),
    ...conflatingTristateReads(applicability.predicate),
  ]) {
    ctx.addIssue({ code: 'custom', message: issue.message, path: ['predicate', ...issue.path] });
  }
  applicability.boosts.forEach((boost, i) => {
    for (const issue of [
      ...validatePredicate(boost.when, applicability.subject_type),
      ...unconstrainedControlReads(boost.when, applicability.subject_type),
      ...conflatingTristateReads(boost.when),
    ]) {
      ctx.addIssue({
        code: 'custom',
        message: issue.message,
        path: ['boosts', i, 'when', ...issue.path],
      });
    }
  });

  if (!applicability.requires_object_types.includes(applicability.subject_type)) {
    ctx.addIssue({
      code: 'custom',
      message: `requires_object_types must include the subject type ${applicability.subject_type}`,
      path: ['requires_object_types'],
    });
  }

  const named = componentTypesNamed(applicability.predicate, applicability.subject_type);
  for (const boost of applicability.boosts) {
    for (const type of componentTypesNamed(boost.when, applicability.subject_type)) {
      named.add(type);
    }
  }
  applicability.requires_component_types.forEach((type, i) => {
    if (!named.has(type)) {
      ctx.addIssue({
        code: 'custom',
        message:
          `requires_component_types names ${type}, which this pattern never tests for — a ` +
          `pre-selection filter the predicate does not use narrows the pattern's reach silently`,
        path: ['requires_component_types', i],
      });
    }
  });

  const reachable = reachableObjectTypes(applicability.predicate, applicability.subject_type);
  for (const boost of applicability.boosts) {
    for (const type of reachableObjectTypes(boost.when, applicability.subject_type)) {
      reachable.add(type);
    }
  }
  applicability.requires_object_types.forEach((type, i) => {
    if (!reachable.has(type)) {
      ctx.addIssue({
        code: 'custom',
        message:
          `requires_object_types names ${type}, which this pattern never reaches — the filter ` +
          `would exclude models for no reason and the pattern would silently never fire`,
        path: ['requires_object_types', i],
      });
    }
  });
});

const ThreatPatternShape = z.object({
  threat_pattern_id: ThreatPatternIdSchema,

  /**
   * The framework this pattern reasons within (docs/DECISIONS.md ZD-009).
   * STRIDE is the IT lens, not the universal one — `stride_category` is
   * required only when this is `'stride'`.
   */
  analysis_lens: AnalysisLensSchema,
  stride_category: StrideCategorySchema.nullable(),

  title: NonEmptyStringSchema,
  description: NonEmptyStringSchema,
  applicability: ApplicabilitySchema,
  unknown_policy: UnknownPolicySchema,

  /**
   * What this pattern says about its own inherent risk (docs/DECISIONS.md
   * ZD-011, narrowed by ZD-058 §5).
   *
   * Typed rather than an untyped detail bag, and **narrower than
   * `RiskDimensions.partial()`**: a pack may not state `confidence` (principle
   * 5) or `control_strength` (a property of the client's system, not of the
   * threat), and must state `primary_loss_magnitude`, which is the only impact
   * input QRS materiality has. `PatternScoringInputsSchema` carries the reasons.
   */
  default_scoring_inputs: PatternScoringInputsSchema,

  /**
   * The mitigations this pattern points at, as **QRS control ids**
   * (docs/DECISIONS.md ZD-032).
   *
   * Never a framework identifier. A pattern citing ISO A.8.24 directly would
   * put the licensing question in every pack and would lose the instruction —
   * published frameworks are governance controls, and what a pattern points at
   * is an engineering one. The catalogue maps outward instead.
   */
  control_requirements: z.array(QrsControlIdSchema).min(1),

  /**
   * The recommendation template this pattern points at — **and nothing defines
   * one** (docs/DECISIONS.md ZD-060 §1).
   *
   * Measured: 65 of 65 patterns carry exactly one id, 36 distinct across the
   * packs, and the only `rec.*` token anywhere outside these declarations is in
   * a schema fixture. `control-references-resolve.test.ts` resolves
   * `control_requirements` into the QRS catalogue and has never had anything to
   * resolve these against, because there is no template catalogue.
   *
   * **This is `linked_controls` surviving ZD-032 untouched**: a pack reference
   * that resolves to nothing, with `Recommendation.template_id` waiting to carry
   * it. It is left in place rather than removed, because it is the concrete form
   * of what the recommendation worker is waiting on — 36 authored templates is a
   * scoped content job with a name, where *"the contextualiser lands"* is an
   * open-ended wait. `recommendation-templates.test.ts` pins the population so
   * the day one is authored, somebody looks.
   */
  recommendation_templates: z.array(NonEmptyStringSchema),

  /**
   * What the pattern needs **in the model in order to fire** — not evidence a
   * client is asked to produce.
   *
   * Recorded because the two are one word apart and the wrong reading is the
   * likely one. Every entry is of the form *"a data flow whose endpoints sit on
   * opposite sides of a boundary"* (65 of 65 patterns carry exactly one), which
   * describes our own predicate's input. `Recommendation.evidence_required` is
   * the field whose name almost matches it, and filling one from the other would
   * write our firing condition into a client's evidence list — ZD-019's
   * manufactured assertion, made likely rather than hypothetical by the naming
   * (ZD-060 §5). What a client can actually be asked for is
   * `QrsControl.evidence`, which the report now carries.
   */
  evidence_requirements: z.array(z.string()),

  /**
   * What a reviewer should understand about the probably-but-not-confirmed
   * case. `unknown_policy` is the machine-readable half of §8.3 rule 3; this is
   * the sentence that goes with it.
   */
  conditional_guidance: z.string().nullable(),
});

/**
 * A STRIDE pattern without a category is as wrong as a non-STRIDE pattern
 * carrying one (docs/DECISIONS.md ZD-009) — both directions are enforced.
 */
export const ThreatPatternSchema = ThreatPatternShape.superRefine((pattern, ctx) => {
  if (pattern.analysis_lens === 'stride' && pattern.stride_category === null) {
    ctx.addIssue({
      code: 'custom',
      message: 'a STRIDE-lens pattern must carry a stride_category',
      path: ['stride_category'],
    });
  }
  if (pattern.analysis_lens !== 'stride' && pattern.stride_category !== null) {
    ctx.addIssue({
      code: 'custom',
      message: `stride_category must be null for a ${pattern.analysis_lens} lens pattern`,
      path: ['stride_category'],
    });
  }
});
export type ThreatPattern = z.infer<typeof ThreatPatternShape>;

const ThreatPackShape = z.object({
  pack_id: PackIdSchema,
  pack_version: SemVerSchema,
  domain: DomainSchema,
  title: NonEmptyStringSchema,
  description: z.string(),
  patterns: z.array(ThreatPatternSchema).min(1),
});

export type ThreatPack = z.infer<typeof ThreatPackShape>;

export const ThreatPackSchema = ThreatPackShape.superRefine((pack, ctx) => {
  const seen = new Set<string>();
  pack.patterns.forEach((pattern, i) => {
    if (seen.has(pattern.threat_pattern_id)) {
      ctx.addIssue({
        code: 'custom',
        message: `duplicate threat_pattern_id ${pattern.threat_pattern_id}`,
        path: ['patterns', i, 'threat_pattern_id'],
      });
    }
    seen.add(pattern.threat_pattern_id);
  });
});

/** A pattern together with the pack it came from — what grounding checks need. */
export interface ResolvedThreatPattern {
  pattern: ThreatPattern;
  pack_id: string;
  pack_version: string;
  domain: Domain;
}

export type ThreatPatternIndex = ReadonlyMap<string, ResolvedThreatPattern>;

/**
 * Flattens packs into a lookup by `threat_pattern_id`.
 *
 * Throws on a collision across packs. A `threat_pattern_id` is the public anchor
 * the reproducibility metric counts and the client's evidence pack cites, so two
 * packs claiming the same id is a build-time error, never a silent last-one-wins.
 */
export function indexThreatPatterns(packs: readonly ThreatPack[]): ThreatPatternIndex {
  const index = new Map<string, ResolvedThreatPattern>();
  for (const pack of packs) {
    for (const pattern of pack.patterns) {
      const existing = index.get(pattern.threat_pattern_id);
      if (existing) {
        throw new Error(
          `threat_pattern_id ${pattern.threat_pattern_id} is declared by both ` +
            `${existing.pack_id}@${existing.pack_version} and ${pack.pack_id}@${pack.pack_version}`,
        );
      }
      index.set(pattern.threat_pattern_id, {
        pattern,
        pack_id: pack.pack_id,
        pack_version: pack.pack_version,
        domain: pack.domain,
      });
    }
  }
  return index;
}

// ---------------------------------------------------------------------------
// What the pack service emits
// ---------------------------------------------------------------------------

export const AttributeRefSchema = z.object({
  object_id: z.string(),
  attribute: NonEmptyStringSchema,
});

/**
 * One pattern applying to one object (docs/DECISIONS.md ZD-031).
 *
 * **It carries no prose**, and that is the decision rather than an omission.
 * `CandidateFindingDraft` requires a `description` and a `rationale`; a
 * deterministic worker filling them with a template would produce text
 * indistinguishable downstream from a model's contextualisation, which is a
 * manufactured assertion of ZD-019's class. So the pack service stops at the
 * point where the *decision* is made, and everything after it is either a
 * transcript rendered from these fields or an LLM's own words — with
 * `CandidateFinding.prose_origin` recording which.
 */
const PatternMatchShape = z.object({
  threat_pattern_id: ThreatPatternIdSchema,
  domain: DomainSchema,
  pack_id: PackIdSchema,
  pack_version: SemVerSchema,

  subject_id: z.string(),
  subject_type: CanonicalObjectTypeSchema,

  /** The subject plus every object the satisfied predicate navigated to (ZD-029 §6). */
  grounded_object_ids: z.array(z.string()).min(1),

  /**
   * `true` — the predicate held. `unknown` — it could not be decided, and the
   * pattern's `unknown_policy` said to surface it anyway. A `false` predicate
   * produces no match at all, so there is no member for it.
   */
  verdict: z.enum(['true', 'unknown']),
  predicate_basis: PredicateBasisSchema,

  /** The attributes the verdict actually rested on. */
  witnesses: z.array(AttributeRefSchema),
  /** The attributes that could not be resolved, where the verdict is `unknown`. */
  undetermined: z.array(AttributeRefSchema),
  /** Titles of the boosts that held — an input to scoring in sds-05. */
  satisfied_boosts: z.array(NonEmptyStringSchema),
});
export type PatternMatch = z.infer<typeof PatternMatchShape>;

/**
 * An undecided verdict must say what it could not decide (ZD-043 §3).
 *
 * **The emittability rule this system actually needs.** A finding with no
 * established action is unrepresentable here for free, because the action comes
 * from the pattern and `ThreatPattern.description` is a `NonEmptyString`. What
 * is not free is the other half: 54 of the corpus's 96 matches are `unknown`,
 * and an `unknown` outcome carries **no witnesses at all** by construction, so
 * everything such a finding says about the client's system is in this list. A
 * match that reached `unknown` and named nothing would render a conditional
 * finding that cannot say what it is conditional on — an absence manufactured
 * by the shape rather than by the documents (ZD-021).
 *
 * **One-directional, and the asymmetry has a reason.** The mirror rule — a
 * `true` verdict must have witnesses — would be wrong: `present()` returns no
 * witness by design, so a pattern that applies to an object structurally
 * legitimately has none, and `grounds` records that as `unestablished` rather
 * than pretending otherwise.
 */
export const PatternMatchSchema = PatternMatchShape.superRefine((match, ctx) => {
  if (match.verdict !== 'unknown' || match.undetermined.length > 0) return;
  ctx.addIssue({
    code: 'custom',
    message:
      'an unknown verdict must name what it could not determine: an unknown outcome carries no ' +
      'witnesses, so this list is everything the finding could say about the system',
    path: ['undetermined'],
  });
});

/** The predicate in English, for a review sheet. Rendered, never hand-glossed (ZD-033). */
export function describePattern(pattern: ThreatPattern): string {
  return describePredicate(pattern.applicability.predicate);
}

/** Re-exported so pack authors and the pack service share one import. */
export type { AttributeRef, PredicateExpr, CanonicalObjectType };
